import { GiphyFetch } from '@giphy/js-fetch-api'
import { AdDecision, AdResponse } from '../ads-endpoint'
import { publicRuntimeConfig } from '../env'

/**
 * Dynamically loads a test JSON file using static import paths for code-splitting.
 * @param adTestValue The test number (e.g., 1 for test1.json)
 * @returns Promise resolving to the JSON data
 */
const loadTestJson = (adTestValue: number) => {
    switch (adTestValue) {
        case 1:
            return import('./test1.json')
        case 2:
            return import('./test2.json')
        case 3:
            return import('./test3.json')
        case 4:
            return import('./test4.json')
        case 5:
            return import('./test5.json')
        case 6:
            return import('./test6.json')
        case 7:
            return import('./test7.json')
        case 8:
            return import('./test8.json')
        case 9:
            return import('./test9.json')
        case 101:
            return import('./legacy-test1.json')
        case 102:
            return import('./legacy-test2.json')
        case 103:
            return import('./legacy-test3.json')
        case 104:
            return import('./legacy-test4.json')
        case 105:
            return import('./legacy-test5.json')
        case 106:
            return import('./legacy-test6.json')
        case 107:
            return import('./legacy-test7.json')
        default:
            throw new Error(`No test response for value ${adTestValue}`)
    }
}
type TestAdResponse = {
    default: AdResponse & { gif_id: string; description: string }
}

/**
 * Loads an ad test response from a mock JSON file and populates the gif field for each decision and subdecision.
 * @param adTestValue The test number (e.g., 1 for test1.json)
 * @returns A tuple: [AdResponse?, errorMessage?]
 */
const getAdTestResponse = async (adTestValue: number): Promise<[AdResponse?, string?]> => {
    try {
        const { default: result } = (await loadTestJson(adTestValue)) as unknown as TestAdResponse
        console.log(`ad test ${result.description}`)

        const processing = new Map<AdDecision, Promise<void>>()

        const fetchGifForDecisionOnce = (
            decision: AdDecision & { gif_id?: string; subdecisions?: AdDecision[] }
        ): Promise<void> => {
            if (processing.has(decision)) {
                return processing.get(decision)!
            }

            const promise = (async () => {
                const promises: Promise<void>[] = []
                if (decision.gif_id) {
                    const fetchGif = async () => {
                        const testGif = await new GiphyFetch(publicRuntimeConfig.apiKey).gif(decision.gif_id as string)
                        decision.gif = testGif.data
                    }
                    promises.push(fetchGif())
                }
                if (decision.subdecisions && Array.isArray(decision.subdecisions)) {
                    promises.push(...decision.subdecisions.map(fetchGifForDecisionOnce))
                }
                await Promise.all(promises)
            })()

            processing.set(decision, promise)
            return promise
        }

        const decisions = Object.values(result?.decisions || {}).filter(
            (d): d is AdDecision => d !== null && d !== undefined
        )
        const decisionPromises = decisions.map(fetchGifForDecisionOnce)
        await Promise.all(decisionPromises)
        return [result as AdResponse]
    } catch (e) {
        console.error(`Failed to load test${adTestValue}.json from ad-mocks`, e)
        return [, `Failed to load test${adTestValue}.json: ${e}`]
    }
}

export default getAdTestResponse
